import { nextTick, onBeforeUnmount, watch, type Ref } from 'vue'

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'

/** Pilha de overlays abertos: o Esc só vale para o de cima (sheet aberto sobre um painel). */
const openStack: symbol[] = []

/**
 * Acessibilidade comum de BottomSheet e FullScreenPanel (spec §6): foco preso, foco inicial
 * dentro do painel, retorno do foco ao elemento que abriu e Esc só no overlay do topo.
 */
export function useOverlayA11y(options: {
  panel: Ref<HTMLElement | null>
  isOpen: Ref<boolean>
  onEscape: () => void
}): { onPanelKeydown: (event: KeyboardEvent) => void } {
  const id = Symbol('overlay')
  let returnFocusTo: HTMLElement | null = null
  let active = false

  const focusables = (): HTMLElement[] =>
    options.panel.value
      ? Array.from(options.panel.value.querySelectorAll<HTMLElement>(FOCUSABLE))
      : []

  function onDocumentKeydown(event: KeyboardEvent): void {
    if (event.key !== 'Escape' || openStack[openStack.length - 1] !== id) return
    event.stopPropagation()
    options.onEscape()
  }

  function onPanelKeydown(event: KeyboardEvent): void {
    if (event.key !== 'Tab') return
    const list = focusables()
    if (!list.length) {
      event.preventDefault()
      options.panel.value?.focus()
      return
    }
    const first = list[0]
    const last = list[list.length - 1]
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault()
      last.focus()
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault()
      first.focus()
    }
  }

  async function activate(): Promise<void> {
    if (active) return
    active = true
    returnFocusTo = document.activeElement instanceof HTMLElement ? document.activeElement : null
    openStack.push(id)
    document.addEventListener('keydown', onDocumentKeydown)
    await nextTick()
    ;(focusables()[0] ?? options.panel.value)?.focus()
  }

  function deactivate(): void {
    if (!active) return
    active = false
    const index = openStack.indexOf(id)
    if (index >= 0) openStack.splice(index, 1)
    document.removeEventListener('keydown', onDocumentKeydown)
    const target = returnFocusTo
    returnFocusTo = null
    if (target && document.contains(target)) target.focus()
  }

  watch(options.isOpen, (open) => (open ? void activate() : deactivate()), { immediate: true })
  onBeforeUnmount(deactivate)

  return { onPanelKeydown }
}
